import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { FORGE_CHAIN_OPTIONS as MASTER_LABEL_CHAIN_OPTIONS } from '../../config/chains';
import { ForgeApiService } from '../../services/ForgeApiService';
import {
  MasterLabelUnresolvedChainSnapshot,
  MasterLabelUnresolvedDashboardResponse,
  MasterLabelUnresolvedFilter,
  MasterLabelUnresolvedRow,
  MasterLabelUnresolvedWorkItem,
} from '../../types';
import {
  itemNumberFromMasterLabelUrl,
  normalizeMasterLabelUrl,
} from '../../utils/masterLabelLinks';

const WORK_ITEMS_PAGE_LIMIT = 50;
const WORK_ITEMS_MAX_RESULTS = 500;
const WORK_ITEMS_MAX_PAGE = Math.ceil(WORK_ITEMS_MAX_RESULTS / WORK_ITEMS_PAGE_LIMIT);
const REFRESH_ALL_CONFIRMATION_MESSAGE =
  "Refresh all chains?\n\nThis starts refreshes for every supported chain and can take a while. If you only need one chain, use that chain's Refresh button instead.";
const UNRESOLVED_FILTER_OPTIONS: { value: MasterLabelUnresolvedFilter; label: string }[] = [
  { value: 'both', label: 'Both' },
  { value: 'pending', label: 'Pending' },
  { value: 'unassigned', label: 'Unassigned' },
];

const CHAIN_BREAKDOWN_METRICS = [
  { key: 'pendingCount', label: 'Pending', color: '#f2cc46' },
  { key: 'unassignedCount', label: 'Unassigned', color: '#41c35b' },
  { key: 'inactiveCount', label: 'Inactive', color: '#8b949e' },
] as const;

function formatNumber(value: number): string {
  return value.toLocaleString();
}

function formatRelativeAge(ageSeconds: number): string {
  if (ageSeconds < 60) return 'just now';
  if (ageSeconds < 3600) return `${Math.floor(ageSeconds / 60)}m ago`;
  if (ageSeconds < 86400) {
    const hours = Math.floor(ageSeconds / 3600);
    const minutes = Math.floor((ageSeconds % 3600) / 60);
    return `${hours}h ${String(minutes).padStart(2, '0')}m ago`;
  }
  const days = Math.floor(ageSeconds / 86400);
  const hours = Math.floor((ageSeconds % 86400) / 3600);
  return hours > 0 ? `${days}d ${hours}h ago` : `${days}d ago`;
}

function interpolateColor(
  start: [number, number, number],
  end: [number, number, number],
  ratio: number
): string {
  const clampedRatio = Math.max(0, Math.min(1, ratio));
  const [red, green, blue] = start.map((channel, index) =>
    Math.round(channel + (end[index] - channel) * clampedRatio)
  );
  return `rgb(${red}, ${green}, ${blue})`;
}

function snapshotAgeColor(ageSeconds: number): string {
  const oneHour = 60 * 60;
  const oneDay = 24 * oneHour;
  const twoDays = 2 * oneDay;

  if (ageSeconds <= oneHour) {
    return interpolateColor([65, 195, 91], [138, 183, 70], ageSeconds / oneHour);
  }
  if (ageSeconds <= oneDay) {
    return interpolateColor(
      [138, 183, 70],
      [238, 163, 55],
      (ageSeconds - oneHour) / (oneDay - oneHour)
    );
  }
  if (ageSeconds <= twoDays) {
    return interpolateColor([238, 163, 55], [232, 82, 63], (ageSeconds - oneDay) / oneDay);
  }
  return interpolateColor(
    [232, 82, 63],
    [255, 55, 85],
    Math.min(1, (ageSeconds - twoDays) / twoDays)
  );
}

function formatTimestamp(value?: string | null): string {
  if (!value) return '-';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function formatSnapshotStatus(status: string): string | null {
  if (status === 'available') return null;
  if (status === 'stale') return 'Stale';
  if (status === 'missing') return 'No cached snapshot';
  return status;
}

function rowLabel(row: MasterLabelUnresolvedRow): string {
  if (row.itemNumber) return row.itemNumber;
  const linkedItemNumber = itemNumberFromMasterLabelUrl(row.masterLabelUrl);
  if (linkedItemNumber) return linkedItemNumber;
  if (row.rowKind === 'item_number' && row.rowKey) return row.rowKey;
  return 'No item number';
}

function masterLabelUrlForRow(row: MasterLabelUnresolvedRow): string | null {
  return normalizeMasterLabelUrl(row.masterLabelUrl, {
    chainId: row.chainId,
    fallbackItemNumber:
      row.itemNumber ?? (row.rowKind === 'item_number' && row.rowKey ? row.rowKey : undefined),
    requireTarget: true,
  });
}

function chainSnapshotWithoutRows(
  chain: MasterLabelUnresolvedChainSnapshot
): MasterLabelUnresolvedChainSnapshot {
  return { ...chain, rows: [] };
}

function recomputeDashboard(
  previous: MasterLabelUnresolvedDashboardResponse,
  updatedChain: MasterLabelUnresolvedChainSnapshot
): MasterLabelUnresolvedDashboardResponse {
  const summaryChain = chainSnapshotWithoutRows(updatedChain);
  const chainExists = previous.chains.some(
    (chain) => chain.chain.chainId === summaryChain.chain.chainId
  );
  const chains = chainExists
    ? previous.chains.map((chain) =>
        chain.chain.chainId === summaryChain.chain.chainId ? summaryChain : chain
      )
    : [...previous.chains, summaryChain];
  return {
    ...previous,
    chains,
    supportedChainIds: previous.supportedChainIds.includes(summaryChain.chain.chainId)
      ? previous.supportedChainIds
      : [...previous.supportedChainIds, summaryChain.chain.chainId].sort(
          (left, right) => left - right
        ),
    topRows: [],
  };
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function isAbortError(error: unknown): boolean {
  return (
    typeof DOMException !== 'undefined' &&
    error instanceof DOMException &&
    error.name === 'AbortError'
  );
}

const headerCellStyle: React.CSSProperties = {
  padding: '8px 10px',
  textAlign: 'left',
  fontSize: 10,
  color: 'var(--text-tertiary)',
  fontWeight: 800,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  borderBottom: '1px solid var(--divider)',
  verticalAlign: 'middle',
};

const bodyCellStyle: React.CSSProperties = {
  padding: '9px 10px',
  borderBottom: '1px solid var(--divider)',
  color: 'var(--text-secondary)',
  fontSize: 12,
  verticalAlign: 'middle',
};

const monoCellStyle: React.CSSProperties = {
  ...bodyCellStyle,
  fontFamily: 'var(--font-mono)',
};

const separatorHeaderCellStyle: React.CSSProperties = {
  ...headerCellStyle,
  borderRight: '1px solid var(--divider)',
};

const separatorBodyCellStyle: React.CSSProperties = {
  ...monoCellStyle,
  borderRight: '1px solid var(--divider)',
};
const leadingSeparatorHeaderCellStyle: React.CSSProperties = {
  ...headerCellStyle,
  borderLeft: '1px solid var(--divider)',
};
const leadingSeparatorBodyCellStyle: React.CSSProperties = {
  ...monoCellStyle,
  borderLeft: '1px solid var(--divider)',
};

const refreshButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text-secondary)',
  borderRadius: 6,
  height: 28,
  padding: '0 10px',
  fontSize: 11,
  fontWeight: 700,
  cursor: 'pointer',
};

const compactMetricStyle: React.CSSProperties = {
  minWidth: 0,
  display: 'grid',
  gap: 3,
  padding: '6px 8px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: 'var(--surface)',
};

const ChainBreakdownPlot: React.FC<{
  chains: MasterLabelUnresolvedChainSnapshot[];
  isLoading: boolean;
  loadError: string | null;
}> = ({ chains, isLoading, loadError }) => {
  const maxChainTotal = Math.max(
    1,
    ...chains.map((chain) =>
      CHAIN_BREAKDOWN_METRICS.reduce((sum, metric) => sum + Number(chain[metric.key] ?? 0), 0)
    )
  );
  const hasMetricData = chains.some((chain) =>
    CHAIN_BREAKDOWN_METRICS.some((metric) => Number(chain[metric.key] ?? 0) > 0)
  );

  const body = (() => {
    if (isLoading) {
      return (
        <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>Loading breakdown...</div>
      );
    }
    if (loadError) {
      return <div style={{ color: 'var(--danger)', fontSize: 12 }}>Breakdown unavailable.</div>;
    }
    if (!hasMetricData) {
      return (
        <div style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>
          No pending, unassigned, or inactive counts in cached snapshots.
        </div>
      );
    }

    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          columnGap: 22,
          rowGap: 10,
        }}
      >
        {chains.map((chain) => {
          const total = CHAIN_BREAKDOWN_METRICS.reduce(
            (sum, metric) => sum + Number(chain[metric.key] ?? 0),
            0
          );
          const barWidth = `${Math.max(0.02, total / maxChainTotal) * 100}%`;
          return (
            <div
              key={chain.chain.chainId}
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(92px, 0.9fr) minmax(112px, 1.4fr) minmax(54px, auto)',
                alignItems: 'center',
                gap: 10,
                minHeight: 34,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    overflow: 'hidden',
                    color: 'var(--text-primary)',
                    fontSize: 12,
                    fontWeight: 850,
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {chain.chain.chainName}
                </div>
                <div style={{ color: 'var(--text-tertiary)', fontSize: 10 }}>
                  chain {chain.chain.chainId}
                </div>
              </div>
              <div
                aria-label={`${chain.chain.chainName} pending unassigned inactive counts`}
                style={{
                  height: 12,
                  borderRadius: 999,
                  background: 'rgba(148, 163, 184, 0.14)',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    width: barWidth,
                    minWidth: total > 0 ? 8 : 0,
                    height: '100%',
                    borderRadius: 999,
                    overflow: 'hidden',
                  }}
                >
                  {CHAIN_BREAKDOWN_METRICS.map((metric) => {
                    const value = Number(chain[metric.key] ?? 0);
                    const segmentWidth = total > 0 ? `${(value / total) * 100}%` : '0%';
                    return (
                      <div
                        key={metric.key}
                        title={`${metric.label}: ${formatNumber(value)}`}
                        style={{
                          width: segmentWidth,
                          minWidth: value > 0 ? 3 : 0,
                          height: '100%',
                          background: metric.color,
                        }}
                      />
                    );
                  })}
                </div>
              </div>
              <div
                style={{
                  color: 'var(--text-secondary)',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  fontWeight: 800,
                  textAlign: 'right',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatNumber(total)}
              </div>
            </div>
          );
        })}
      </div>
    );
  })();

  return (
    <div className="forge-card rdc-dashboard-card">
      <div
        className="forge-card-title rdc-dashboard-card-title rdc-dashboard-table-title"
        style={{
          marginBottom: 10,
        }}
      >
        <div>Unresolved by chain</div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {CHAIN_BREAKDOWN_METRICS.map((metric) => (
            <div
              key={metric.key}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 7,
                color: 'var(--text-secondary)',
                fontSize: 11,
                fontWeight: 800,
              }}
            >
              <span
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  background: metric.color,
                }}
              />
              {metric.label}
            </div>
          ))}
        </div>
      </div>
      {body}
    </div>
  );
};

export const MasterLabelUnresolvedDashboard: React.FC = () => {
  const { token, hasInternalAccess } = useAuthContext();
  const { showErrorToast, showSuccessToast } = useNotificationContext();
  const [dashboard, setDashboard] = useState<MasterLabelUnresolvedDashboardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [workItems, setWorkItems] = useState<MasterLabelUnresolvedWorkItem[]>([]);
  const [workItemsPage, setWorkItemsPage] = useState(1);
  const [workItemsHasNext, setWorkItemsHasNext] = useState(false);
  const [isLoadingWorkItems, setIsLoadingWorkItems] = useState(false);
  const [workItemsError, setWorkItemsError] = useState<string | null>(null);
  const [workItemsReloadKey, setWorkItemsReloadKey] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [refreshingChainIds, setRefreshingChainIds] = useState<Set<number>>(() => new Set());
  const [isRefreshingAll, setIsRefreshingAll] = useState(false);
  const [refreshAllProgress, setRefreshAllProgress] = useState<{
    completed: number;
    total: number;
  } | null>(null);
  const [workItemsChainFilter, setWorkItemsChainFilter] = useState<'all' | number>('all');
  const [unresolvedFilter, setUnresolvedFilter] = useState<MasterLabelUnresolvedFilter>('both');
  const [currentTime, setCurrentTime] = useState(() => new Date());

  useEffect(() => {
    let cancelled = false;
    const loadDashboard = async () => {
      setIsLoading(true);
      try {
        const nextDashboard = await ForgeApiService.fetchMasterLabelUnresolved(token);
        if (!cancelled) {
          setLoadError(null);
          setDashboard({
            ...nextDashboard,
            chains: (nextDashboard.chains ?? []).map(chainSnapshotWithoutRows),
            topRows: [],
          });
        }
      } catch (error) {
        if (!cancelled) {
          const message =
            error instanceof Error ? error.message : 'Error loading Master Label unresolved list';
          setLoadError(message);
          setDashboard(null);
          setWorkItems([]);
          setWorkItemsHasNext(false);
          showErrorToast(message);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadDashboard();
    return () => {
      cancelled = true;
    };
  }, [showErrorToast, token]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentTime(new Date());
    }, 30000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  const totals = useMemo(() => {
    const chains = dashboard?.chains ?? [];
    return chains.reduce(
      (acc, chain) => ({
        crops: acc.crops + (chain.cropCount ?? 0),
        inDesign: acc.inDesign + (chain.inDesignCount ?? 0),
        carton: acc.carton + (chain.cartonCount ?? 0),
        pending: acc.pending + chain.pendingCount,
        unassigned: acc.unassigned + chain.unassignedCount,
        inactive: acc.inactive + (chain.inactiveCount ?? 0),
        unresolved: acc.unresolved + chain.unresolvedCount,
      }),
      {
        crops: 0,
        inDesign: 0,
        carton: 0,
        pending: 0,
        unassigned: 0,
        inactive: 0,
        unresolved: 0,
      }
    );
  }, [dashboard]);

  const chains = useMemo(() => dashboard?.chains ?? [], [dashboard?.chains]);

  const chainOptions = useMemo(() => {
    const supportedChainIds = new Set(
      dashboard?.supportedChainIds ?? chains.map((chain) => chain.chain.chainId)
    );
    return [...supportedChainIds]
      .sort((left, right) => left - right)
      .map((chainId) => {
        const loadedChain = chains.find((chain) => chain.chain.chainId === chainId);
        const configuredChain = MASTER_LABEL_CHAIN_OPTIONS.find((chain) => chain.id === chainId);
        return {
          chainId,
          label: loadedChain?.chain.chainName ?? configuredChain?.label ?? `Chain ${chainId}`,
          timeZone: loadedChain?.chain.timeZone ?? 'UTC',
        };
      });
  }, [chains, dashboard?.supportedChainIds]);

  const refreshChainIds = useMemo(() => chainOptions.map((chain) => chain.chainId), [chainOptions]);
  const selectedWorkItemsChainId = workItemsChainFilter === 'all' ? null : workItemsChainFilter;

  useEffect(() => {
    setWorkItemsPage(1);
  }, [workItemsChainFilter, unresolvedFilter]);

  useEffect(() => {
    let cancelled = false;
    const abortController = new AbortController();
    const loadWorkItems = async () => {
      setIsLoadingWorkItems(true);
      try {
        const response = await ForgeApiService.fetchMasterLabelUnresolvedWorkItems(token, {
          chainId: selectedWorkItemsChainId,
          unresolvedFilter,
          page: workItemsPage,
          limit: WORK_ITEMS_PAGE_LIMIT,
          signal: abortController.signal,
        });
        if (!cancelled) {
          setWorkItems(response.items ?? []);
          setWorkItemsHasNext(response.hasNext);
          setWorkItemsError(null);
        }
      } catch (error) {
        if (!cancelled && !isAbortError(error)) {
          const message =
            error instanceof Error
              ? error.message
              : 'Error loading Master Label unresolved work items';
          setWorkItems([]);
          setWorkItemsHasNext(false);
          setWorkItemsError(message);
          showErrorToast(message);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingWorkItems(false);
        }
      }
    };

    if (!isLoading && !loadError) {
      void loadWorkItems();
    }

    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [
    isLoading,
    loadError,
    selectedWorkItemsChainId,
    showErrorToast,
    token,
    unresolvedFilter,
    workItemsPage,
    workItemsReloadKey,
  ]);

  const reloadWorkItems = () => {
    setWorkItemsPage(1);
    setWorkItemsReloadKey((current) => current + 1);
  };

  const beginRefreshingChain = (chainId: number) => {
    setRefreshingChainIds((current) => new Set(current).add(chainId));
  };

  const finishRefreshingChain = (chainId: number) => {
    setRefreshingChainIds((current) => {
      const next = new Set(current);
      next.delete(chainId);
      return next;
    });
  };

  const handleRefreshChain = async (chainId: number) => {
    beginRefreshingChain(chainId);
    try {
      const updatedChain = await ForgeApiService.refreshMasterLabelUnresolved(token, chainId);
      setDashboard((current) =>
        current
          ? recomputeDashboard(current, updatedChain)
          : {
              chains: [chainSnapshotWithoutRows(updatedChain)],
              topRows: [],
              supportedChainIds: [chainId],
            }
      );
      setLoadError(null);
      reloadWorkItems();
    } catch (error) {
      showErrorToast(
        error instanceof Error ? error.message : 'Error refreshing Master Label unresolved snapshot'
      );
    } finally {
      finishRefreshingChain(chainId);
    }
  };

  const handleRefreshAll = async () => {
    if (refreshChainIds.length === 0 || isRefreshingAll) return;
    if (!window.confirm(REFRESH_ALL_CONFIRMATION_MESSAGE)) return;

    setIsRefreshingAll(true);
    setRefreshAllProgress({ completed: 0, total: refreshChainIds.length });
    const failures: string[] = [];

    for (const [index, chainId] of refreshChainIds.entries()) {
      beginRefreshingChain(chainId);
      try {
        const updatedChain = await ForgeApiService.refreshMasterLabelUnresolved(token, chainId);
        setDashboard((current) =>
          current
            ? recomputeDashboard(current, updatedChain)
            : {
                chains: [chainSnapshotWithoutRows(updatedChain)],
                topRows: [],
                supportedChainIds: [chainId],
              }
        );
        setLoadError(null);
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : 'Error refreshing Master Label unresolved snapshot';
        failures.push(`chain ${chainId}: ${message}`);
      } finally {
        finishRefreshingChain(chainId);
        setRefreshAllProgress({ completed: index + 1, total: refreshChainIds.length });
      }
    }

    setIsRefreshingAll(false);
    setRefreshAllProgress(null);
    if (failures.length > 0) {
      showErrorToast(
        `Refresh all finished with ${failures.length}/${refreshChainIds.length} failures. ${failures[0]}`
      );
      return;
    }

    reloadWorkItems();
  };

  const handleExportWorkItems = async () => {
    setIsExporting(true);
    try {
      const { blob, filename } = await ForgeApiService.exportMasterLabelUnresolvedWorkItems(token, {
        chainId: selectedWorkItemsChainId,
        unresolvedFilter,
      });
      downloadBlob(blob, filename);
      showSuccessToast('Master Label unresolved CSV exported');
    } catch (error) {
      showErrorToast(
        error instanceof Error ? error.message : 'Error exporting Master Label unresolved rows'
      );
    } finally {
      setIsExporting(false);
    }
  };

  const taskRows = useMemo(() => workItems.map((item) => item.row), [workItems]);
  const workItemsCanGoNext = workItemsHasNext && workItemsPage < WORK_ITEMS_MAX_PAGE;
  const summaryMetrics = [
    ['Unresolved', totals.unresolved],
    ['Pending', totals.pending],
    ['Unassigned', totals.unassigned],
    ['Inactive', totals.inactive],
    ['Crops', totals.crops],
  ] as const;
  const chainTableColumnCount = hasInternalAccess ? 9 : 8;

  const chainsCard = (
    <div className="forge-card rdc-dashboard-card">
      <div
        className="forge-card-title rdc-dashboard-card-title rdc-dashboard-table-title"
        style={{
          marginBottom: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <span>Chains</span>
        {hasInternalAccess ? (
          <button
            type="button"
            style={{
              ...refreshButtonStyle,
              height: 30,
              opacity: isRefreshingAll || refreshChainIds.length === 0 ? 0.65 : 1,
              cursor: isRefreshingAll || refreshChainIds.length === 0 ? 'not-allowed' : 'pointer',
            }}
            disabled={isRefreshingAll || refreshChainIds.length === 0}
            onClick={() => void handleRefreshAll()}
          >
            <RefreshOutlinedIcon style={{ fontSize: 15 }} />
            {isRefreshingAll && refreshAllProgress
              ? `Refreshing ${refreshAllProgress.completed}/${refreshAllProgress.total}`
              : 'Refresh all'}
          </button>
        ) : null}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={headerCellStyle}>Chain</th>
              <th style={headerCellStyle}>Snapshot</th>
              <th style={leadingSeparatorHeaderCellStyle}>Crops</th>
              <th style={headerCellStyle}>In Design</th>
              <th style={headerCellStyle}>Carton</th>
              <th style={separatorHeaderCellStyle}>Inactive</th>
              <th style={headerCellStyle}>Pending</th>
              <th style={separatorHeaderCellStyle}>Unassigned</th>
              {hasInternalAccess ? <th style={headerCellStyle}>Action</th> : null}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={chainTableColumnCount} style={bodyCellStyle}>
                  Loading snapshots...
                </td>
              </tr>
            ) : loadError ? (
              <tr>
                <td
                  colSpan={chainTableColumnCount}
                  style={{ ...bodyCellStyle, color: 'var(--danger)' }}
                >
                  {loadError}
                </td>
              </tr>
            ) : (
              chains.map((chain) => {
                const isRefreshing = refreshingChainIds.has(chain.chain.chainId);
                const isRefreshDisabled = isRefreshingAll || isRefreshing;
                return (
                  <tr key={chain.chain.chainId}>
                    <td style={bodyCellStyle}>
                      <div style={{ color: 'var(--text-primary)', fontWeight: 800 }}>
                        {chain.chain.chainName}
                      </div>
                      <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
                        chain {chain.chain.chainId}
                      </div>
                    </td>
                    <td style={bodyCellStyle}>
                      {chain.snapshot ? (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span
                              style={{
                                color: snapshotAgeColor(chain.snapshot.ageSeconds),
                                fontWeight: 800,
                              }}
                            >
                              {formatRelativeAge(chain.snapshot.ageSeconds)}
                            </span>
                            {formatSnapshotStatus(chain.status) ? (
                              <span
                                style={{
                                  color:
                                    chain.status === 'stale'
                                      ? 'var(--warning)'
                                      : 'var(--text-tertiary)',
                                  fontSize: 10,
                                  fontWeight: 800,
                                  textTransform: 'uppercase',
                                }}
                              >
                                {formatSnapshotStatus(chain.status)}
                              </span>
                            ) : null}
                          </div>
                          <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
                            {formatTimestamp(chain.snapshot.computedAtUtc)}
                          </div>
                        </>
                      ) : (
                        <span style={{ color: 'var(--warning)' }}>
                          {formatSnapshotStatus(chain.status) ?? 'No cached snapshot'}
                        </span>
                      )}
                    </td>
                    <td style={leadingSeparatorBodyCellStyle}>
                      {formatNumber(chain.cropCount ?? 0)}
                    </td>
                    <td style={monoCellStyle}>{formatNumber(chain.inDesignCount ?? 0)}</td>
                    <td style={monoCellStyle}>{formatNumber(chain.cartonCount ?? 0)}</td>
                    <td style={separatorBodyCellStyle}>{formatNumber(chain.inactiveCount ?? 0)}</td>
                    <td style={monoCellStyle}>{formatNumber(chain.pendingCount)}</td>
                    <td style={separatorBodyCellStyle}>{formatNumber(chain.unassignedCount)}</td>
                    {hasInternalAccess ? (
                      <td style={bodyCellStyle}>
                        <button
                          type="button"
                          style={{
                            ...refreshButtonStyle,
                            opacity: isRefreshDisabled ? 0.65 : 1,
                            cursor: isRefreshDisabled ? 'not-allowed' : 'pointer',
                          }}
                          disabled={isRefreshDisabled}
                          onClick={() => void handleRefreshChain(chain.chain.chainId)}
                        >
                          <RefreshOutlinedIcon style={{ fontSize: 15 }} />
                          {isRefreshing ? 'Refreshing...' : 'Refresh'}
                        </button>
                      </td>
                    ) : null}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const taskListCard = (
    <div className="forge-card rdc-dashboard-card">
      <div
        className="forge-card-title rdc-dashboard-card-title rdc-dashboard-table-title"
        style={{
          marginBottom: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div className="rdc-dashboard-section-heading">
          <WarningAmberOutlinedIcon style={{ fontSize: 18, color: 'var(--warning)' }} />
          <span>Unresolved tasks</span>
          <span className="rdc-dashboard-count-pill">{formatNumber(taskRows.length)} rows</span>
          <span className="rdc-dashboard-count-pill">Page {workItemsPage}</span>
          <span className="rdc-dashboard-count-pill">
            {WORK_ITEMS_PAGE_LIMIT} per page, up to {formatNumber(WORK_ITEMS_MAX_RESULTS)}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            aria-label="Unresolved chain filter"
            value={workItemsChainFilter}
            disabled={isLoading}
            onChange={(event) => {
              const value = event.target.value;
              setWorkItemsChainFilter(value === 'all' ? 'all' : Number(value));
            }}
            style={{
              height: 32,
              minWidth: 180,
              borderRadius: 6,
              border: '1px solid var(--border)',
              background: 'var(--surface)',
              color: 'var(--text-primary)',
              padding: '0 10px',
              fontSize: 12,
              fontWeight: 700,
              opacity: isLoading ? 0.7 : 1,
            }}
          >
            <option value="all">{isLoading ? 'Loading chains...' : 'All supported chains'}</option>
            {chainOptions.map((chain) => (
              <option key={chain.chainId} value={chain.chainId}>
                {chain.label}
              </option>
            ))}
          </select>
          <div
            role="group"
            aria-label="Unresolved type filter"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              height: 32,
              padding: 3,
              border: '1px solid var(--border)',
              borderRadius: 8,
              background: 'var(--surface)',
            }}
          >
            {UNRESOLVED_FILTER_OPTIONS.map((option) => {
              const isSelected = unresolvedFilter === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => setUnresolvedFilter(option.value)}
                  style={{
                    height: 24,
                    minWidth: option.value === 'unassigned' ? 92 : 62,
                    border: 0,
                    borderRadius: 6,
                    background: isSelected ? 'rgba(65, 195, 91, 0.14)' : 'transparent',
                    color: isSelected ? '#2fb357' : 'var(--text-secondary)',
                    fontSize: 12,
                    fontWeight: 850,
                    cursor: 'pointer',
                    padding: '0 8px',
                  }}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            style={{
              ...refreshButtonStyle,
              height: 32,
              width: 112,
              opacity: isExporting || isLoading || Boolean(loadError) ? 0.65 : 1,
              cursor: isExporting || isLoading || Boolean(loadError) ? 'not-allowed' : 'pointer',
            }}
            disabled={isExporting || isLoading || Boolean(loadError)}
            onClick={() => void handleExportWorkItems()}
          >
            <FileDownloadOutlinedIcon style={{ fontSize: 15 }} />
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </button>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button
              type="button"
              aria-label="Previous unresolved page"
              style={{
                ...refreshButtonStyle,
                height: 32,
                width: 34,
                padding: 0,
                opacity: workItemsPage <= 1 || isLoadingWorkItems ? 0.55 : 1,
                cursor: workItemsPage <= 1 || isLoadingWorkItems ? 'not-allowed' : 'pointer',
              }}
              disabled={workItemsPage <= 1 || isLoadingWorkItems}
              onClick={() => setWorkItemsPage((page) => Math.max(1, page - 1))}
            >
              <ChevronLeftIcon style={{ fontSize: 18 }} />
            </button>
            <button
              type="button"
              aria-label="Next unresolved page"
              style={{
                ...refreshButtonStyle,
                height: 32,
                width: 34,
                padding: 0,
                opacity: !workItemsCanGoNext || isLoadingWorkItems ? 0.55 : 1,
                cursor: !workItemsCanGoNext || isLoadingWorkItems ? 'not-allowed' : 'pointer',
              }}
              disabled={!workItemsCanGoNext || isLoadingWorkItems}
              onClick={() => setWorkItemsPage((page) => Math.min(WORK_ITEMS_MAX_PAGE, page + 1))}
            >
              <ChevronRightIcon style={{ fontSize: 18 }} />
            </button>
          </div>
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={headerCellStyle}>Chain</th>
              <th style={headerCellStyle}>Item</th>
              <th style={headerCellStyle}>Pending</th>
              <th style={separatorHeaderCellStyle}>Unassigned</th>
              <th style={headerCellStyle}>Inactive</th>
              <th style={headerCellStyle}>Crops</th>
              <th style={headerCellStyle}>Designs</th>
              <th style={headerCellStyle}>Confirmed</th>
              <th style={headerCellStyle}>Action</th>
            </tr>
          </thead>
          <tbody>
            {isLoadingWorkItems || taskRows.length === 0 ? (
              <tr>
                <td colSpan={9} style={bodyCellStyle}>
                  {isLoadingWorkItems
                    ? 'Loading tasks...'
                    : workItemsError
                      ? workItemsError
                      : loadError
                        ? 'Dashboard failed to load.'
                        : 'No unresolved tasks in cached snapshots.'}
                </td>
              </tr>
            ) : (
              taskRows.map((row) => {
                const masterLabelUrl = masterLabelUrlForRow(row);
                return (
                  <tr key={`${row.chainId}:${row.rowKind}:${row.rowKey}`}>
                    <td style={bodyCellStyle}>{row.chainName}</td>
                    <td style={monoCellStyle}>
                      {masterLabelUrl ? (
                        <Link
                          to={masterLabelUrl}
                          style={{
                            color: 'var(--accent)',
                            fontWeight: 800,
                            textDecoration: 'none',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {rowLabel(row)}
                        </Link>
                      ) : (
                        <span style={{ color: 'var(--text-primary)', fontWeight: 800 }}>
                          {rowLabel(row)}
                        </span>
                      )}
                    </td>
                    <td style={monoCellStyle}>{formatNumber(row.pendingCount)}</td>
                    <td style={separatorBodyCellStyle}>{formatNumber(row.unassignedCount)}</td>
                    <td style={monoCellStyle}>{formatNumber(row.inactiveCount ?? 0)}</td>
                    <td style={monoCellStyle}>{formatNumber(row.totalCropCount ?? 0)}</td>
                    <td style={monoCellStyle}>{formatNumber(row.designCount ?? 0)}</td>
                    <td style={monoCellStyle}>{formatNumber(row.confirmedDesignCount ?? 0)}</td>
                    <td style={bodyCellStyle}>
                      {masterLabelUrl ? (
                        <Link
                          to={masterLabelUrl}
                          style={{
                            color: 'var(--accent)',
                            fontWeight: 800,
                            textDecoration: 'none',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Open Master Label
                        </Link>
                      ) : (
                        <span style={{ color: 'var(--text-tertiary)' }}>-</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (!hasInternalAccess) {
    return (
      <div className="page-content page-content--wide rdc-dashboard-page">
        {chainsCard}
        {taskListCard}
      </div>
    );
  }

  return (
    <div className="page-content page-content--wide rdc-dashboard-page">
      <div className="forge-card rdc-dashboard-hero">
        <div className="rdc-dashboard-hero-grid">
          <div>
            <div className="rdc-dashboard-eyebrow">Master Label</div>
            <div className="rdc-dashboard-title">Master Label unresolved</div>
            <div className="rdc-dashboard-copy">
              Cached per-chain snapshots ranked by unresolved item groups needing Master Label
              cleanup. Refresh a chain to regenerate its current snapshot.
            </div>
            <div style={{ marginTop: 6, color: 'var(--text-tertiary)', fontSize: 11 }}>
              Browser time: {formatTimestamp(currentTime.toISOString())} · Supported chains:{' '}
              {dashboard?.supportedChainIds?.join(', ') || '-'}
            </div>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(112px, 1fr))',
              gap: 8,
            }}
          >
            {summaryMetrics.map(([label, value]) => (
              <div key={label} style={compactMetricStyle}>
                <div
                  style={{
                    overflow: 'hidden',
                    color: 'var(--text-tertiary)',
                    fontSize: 10,
                    fontWeight: 850,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </div>
                <div
                  style={{
                    color: 'var(--text-primary)',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 16,
                    fontWeight: 900,
                    lineHeight: 1.05,
                  }}
                >
                  {formatNumber(Number(value))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <ChainBreakdownPlot chains={chains} isLoading={isLoading} loadError={loadError} />

      {chainsCard}

      {taskListCard}
    </div>
  );
};
